import { Core, Decorative } from "@faire/design-tokens";
import { delay } from "@faire/web/common/delay";
import { getLocation } from "@faire/web/common/globals/getLocation";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Column } from "@faire/web/slate/Layout";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect, useRef } from "react";

import { FooterSEOContent } from "@faire/retailer/components/Footer/FooterSEOContent";
import { TestimonialsCardSection } from "@faire/retailer/components/TestimonialsCardSection/TestimonialsCardSection";
import { useTopOfPageContentStore } from "@faire/retailer/stores/ui/TopOfPageContentStore";
import { getJobOpeningCount } from "@faire/retailer/views/careers/__internal__/common/common";
import {
  CareersFooterImage,
  CareersHeroImage,
} from "@faire/retailer/views/careers/__internal__/components/FullWidthImages";
import { JobOpeningsSection } from "@faire/retailer/views/careers/__internal__/components/JobOpeningsSection";
import { FAIRE_EMPLOYEES } from "@faire/retailer/views/careers/__internal__/LifeatFaire/faireEmployees";
import { LandingPageHeroSection } from "@faire/retailer/views/careers/__internal__/LifeatFaire/LandingPageHeroSection";
import { OurBenefitsImagesSection } from "@faire/retailer/views/careers/__internal__/LifeatFaire/OurBenefitsImagesSection";
import { OurBenefitsSection } from "@faire/retailer/views/careers/__internal__/LifeatFaire/OurBenefitsSection";
import { OurValuesSection } from "@faire/retailer/views/careers/__internal__/LifeatFaire/OurValuesSection";
import { ValuePropositionSection } from "@faire/retailer/views/careers/__internal__/LifeatFaire/ValuePropositionSection";

import { useDepartments } from "../common/useDepartments";

export const CareersLandingPage = () => {
  const { strictLocalize } = useStrictLocalization();
  const { departments, isLoading } = useDepartments(strictLocalize).EMPLOYMENT;
  const { headerPlusPromoBannerHeight } = useTopOfPageContentStore([
    "headerPlusPromoBannerHeight",
  ]);

  const rootRef = useRef<HTMLDivElement>(null);

  const jobCount = getJobOpeningCount(departments);

  useEffect(() => {
    const { hash } = getLocation() ?? {};
    if (hash) {
      const element = rootRef.current?.querySelector(hash);
      if (element) {
        delay(500).then(() => {
          const yCoordinate =
            element.getBoundingClientRect().top +
            (getWindow()?.pageYOffset ?? 0);
          getWindow()?.scrollTo?.({
            top: yCoordinate - headerPlusPromoBannerHeight,
            behavior: "smooth",
          });
        });
      }
    }
  }, [headerPlusPromoBannerHeight, rootRef]);

  return (
    <Column ref={rootRef}>
      <LandingPageHeroSection openingsCount={jobCount} isLoading={isLoading} />
      <ValuePropositionSection />
      <CareersHeroImage />
      <OurValuesSection />
      <TestimonialsCardSection
        testimonials={FAIRE_EMPLOYEES(strictLocalize)}
        title={
          <StrictLocalMsg
            defaultMessage="Employee Voices"
            description={{
              text: "Title for careers testimonials carousel",
              img: "https://cdn.faire.com/fastly/85a32ef5968a5446ae852e06daa5c740a8c6281a7bf5f2c67ab88c2a75d54ee9.png",
            }}
          />
        }
        titleColor={Core.text.subdued}
        backgroundColor={Decorative.tint.neutral}
        textColor={Core.text.default}
      />
      <OurBenefitsImagesSection />
      <OurBenefitsSection />
      <JobOpeningsSection />
      <CareersFooterImage />
      <FooterSEOContent />
    </Column>
  );
};
